import { isEmpty, keys } from "lodash";
import { stringifyUrl } from "query-string";
import React, { useCallback, useMemo } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import CenteredLoading from "../../../components/CenteredLoading";
import FeatureExplanation from "../../../components/FeatureExplanation";
import DisplayLabels from "../../../components/labels/DisplayLabels";
import FilterLabels from "../../../components/labels/FilterLabels";
import VirtualScroll from "../../../components/VirtualScroll";
import { useAuthentication } from "../../../context/authentication";
import { useFilteredList } from "../../../context/filteredList";
import { getSmartLabelFromKey } from "../../../context/filteredList/FilteredListProvider";
import { useHotspots } from "../../../context/hotspots";
import { useInstalls } from "../../../context/installs";
import { useLabels } from "../../../context/labels";
import { useLoading } from "../../../context/loading";
import { useLocations } from "../../../context/locations";
import { useMembers } from "../../../context/members/MembersProvider";
import { useViewport } from "../../../context/viewport";
import { useWorkspaces } from "../../../context/workspaces/WorkspacesProvider";
import classNames from "../../../utils/classNames";
import { avatarsApi, breakpoints, Entity } from "../../../utils/constants";
import { formatHotspotName } from "../../../utils/hotspotUtils";
import { truncateText } from "../../../utils/text";
import { Install } from "../../../utils/types";
import useFilteredLabels, {
  getFilteredLabels,
  LabelFilterKey
} from "../../../utils/useFilteredLabels";
import { installsExplanationParagraphs } from "../../Workspace/InstallsData";
import InstallActionsButton from "../../Workspace/InstallsData/InstallActionsButton";
import LockedWorkspace from "../../Workspace/LockedWorkspace";
import LockedWorkspaceByCommunityPlan from "../../Workspace/LockedWorkspaceByCommunityPlan";
import LockedWorkspaceByMembers from "../../Workspace/LockedWorkspaceByMembers";
import useSideMenu from "./useSideMenu";

interface Props {}

const InstallsList: React.FC<Props> = () => {
  const { wrapActionWithAuth } = useAuthentication();
  const location = useLocation();
  const { currentWorkspaceAllowedForCommunityPlan } = useWorkspaces();
  const { hotspotsMap } = useHotspots();
  const { locations } = useLocations();
  const { installs, toggleInstallSidebar } = useInstalls();
  const history = useHistory();
  const toggleSideMenu = useSideMenu((state) => state.toggleSideMenu);
  const { getIsReady } = useLoading();
  const { filteredList, smartLabelFilters } = useFilteredList();
  const { labels } = useLabels();
  const { width } = useViewport();
  const isMobile = useMemo(() => width < breakpoints.md, [width]);
  const filteredLabels = useFilteredLabels((state) => state.filteredLabels);
  const { currentWorkspaceIsLocked } = useWorkspaces();
  const { membersLimitExceeded } = useMembers();

  const filteredInstallsLabels = useMemo(
    () =>
      getFilteredLabels(
        filteredLabels[LabelFilterKey.mapInstalls],
        labels,
        smartLabelFilters[Entity.install],
        Entity.install
      ),
    [filteredLabels, labels, smartLabelFilters]
  );

  const filteredInstalls = useMemo(
    () => filteredList[Entity.install],
    [filteredList]
  );

  const detailsPageMatch = matchPath<{
    id?: string;
    section?: string;
    subsection?: string;
  }>(location.pathname, {
    path: ["/installs/:id/:section/:subsection", "/installs/:id/:section"]
  });

  const toggleInstall = useCallback(
    (install: Install) => {
      if (isMobile) toggleSideMenu();

      const {
        id,
        section,
        subsection
      }: {
        id?: string;
        section?: string;
        subsection?: string;
      } = detailsPageMatch?.params || {};

      if (!install || install?.uuid === id) {
        history.push("/installs");
      } else {
        let sectionPath = "info";
        if (section) {
          sectionPath = subsection ? `${section}/${subsection}` : section;
        }
        history.push(`/installs/${install?.uuid}/${sectionPath}`);
      }
    },
    [detailsPageMatch?.params, history, isMobile, toggleSideMenu]
  );

  const renderInstall = useCallback(
    (index: number) => {
      const install = filteredInstalls[index];
      const hotspot = hotspotsMap[install.hotspot_uuid];
      const location = locations[install.location_uuid];

      return (
        <div
          key={install.uuid}
          className={classNames(
            "relative px-6 py-5 flex items-center space-x-3 cursor-pointer hover:ring-2 ring-inset ring-pink-500 border-b border-gray-200 dark:border-gray-500  dark:ring-white",
            detailsPageMatch?.params?.id === install.uuid && "ring-2"
          )}
          onClick={() => toggleInstall(install)}
        >
          <div className="flex-shrink-0">
            <img
              className="h-10 w-10 rounded-full"
              src={stringifyUrl({
                url: `${avatarsApi}/bottts/${hotspot?.id}.svg`,
                query: { w: 40, h: 40 }
              })}
              alt="Hotspot avatar"
            />
          </div>
          <div className="flex-1 min-w-0 focus:outline-none">
            <div className="flex items-center">
              <div className="flex flex-col items-start w-full">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate items-center">
                  {hotspot
                    ? formatHotspotName(hotspot)
                    : truncateText(install.hotspot_uuid, 20)}
                </p>
                <p className="text-sm font-normal text-gray-500 truncate dark:text-gray-400 w-full overflow-ellipsis overflow-hidden">
                  {location?.name}
                </p>
              </div>
            </div>

            <DisplayLabels linkedUuid={install.uuid} size="small" />
          </div>
          <InstallActionsButton
            install={install}
            toggleEditInstallForm={toggleInstallSidebar}
          />
        </div>
      );
    },
    [
      filteredInstalls,
      hotspotsMap,
      locations,
      detailsPageMatch?.params?.id,
      toggleInstallSidebar,
      toggleInstall
    ]
  );

  const isLoading = useMemo(
    () => isEmpty(installs) && !getIsReady(["installs"]),
    [getIsReady, installs]
  );

  const renderFilterSection = useMemo(
    () => (
      <div>
        <div className="flex-1 flex px-4 pb-2">
          <FilterLabels
            className="mt-2 w-full"
            entity={Entity.install}
            filteredLabels={filteredInstallsLabels}
            smartLabels={keys(smartLabelFilters[Entity.install]).map((key) =>
              getSmartLabelFromKey(key, Entity.install)
            )}
            filterKey={LabelFilterKey.mapInstalls}
          />
        </div>
      </div>
    ),
    [filteredInstallsLabels, smartLabelFilters]
  );

  if (currentWorkspaceIsLocked) {
    return (
      <LockedWorkspace
        title="Payment failed"
        paragraphs={[
          "Your subscription payment failed. Please resolve the issue with your payment method."
        ]}
        buttonText="Update payment details"
        buttonLink="/workspaces/settings/billing"
      />
    );
  }

  if (!currentWorkspaceAllowedForCommunityPlan) {
    return <LockedWorkspaceByCommunityPlan />;
  }

  if (membersLimitExceeded) {
    return <LockedWorkspaceByMembers />;
  }

  if (isLoading) {
    return <CenteredLoading className="h-full" />;
  }

  if (isEmpty(installs)) {
    return (
      <FeatureExplanation
        paragraphs={installsExplanationParagraphs.concat(
          "Locations and hotspots linked to the listed installs are available on the map." as any
        )}
      >
        <button
          type="button"
          className="mt-6 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
          onClick={wrapActionWithAuth(() => toggleInstallSidebar())}
        >
          Add install
        </button>
      </FeatureExplanation>
    );
  }

  if (isEmpty(filteredInstalls)) {
    return (
      <>
        {renderFilterSection}

        <div className="z-10 sticky top-0 border-b border-t border-gray-200 bg-gray-50 text-gray-500 px-6 py-1 flex items-center justify-between dark:border-gray-500 dark:bg-gray-800 dark:text-gray-300">
          <div className="text-sm font-medium">0 Installs</div>
        </div>

        <div className="px-6 pt-6 pb-4">
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            No installs to show.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      {renderFilterSection}

      <div className="z-10 sticky top-0 border-t border-b border-gray-200 bg-gray-50 text-gray-500 px-6 py-1 flex items-center justify-between dark:border-gray-500 dark:bg-gray-800 dark:text-gray-300">
        <div className="text-sm font-medium">
          {filteredInstalls.length.toLocaleString(undefined)} install
          {filteredInstalls.length !== 1 && "s"}
        </div>
      </div>

      <VirtualScroll
        className="overflow-y-auto"
        minItemHeight={80}
        totalLength={filteredInstalls.length}
        renderItem={renderInstall}
      />
    </>
  );
};

export default InstallsList;
